import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const Modal = ({ 
  title, 
  message, 
  type = 'info', 
  onConfirm, 
  onCancel, 
  onClose,
  confirmText = 'OK',
  cancelText = 'Cancel'
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Small delay to trigger entry animation
    const timer = setTimeout(() => setIsAnimating(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300); // Wait for animation to finish
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    handleClose();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    handleClose();
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-12 h-12 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-12 h-12 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-12 h-12 text-yellow-500" />;
      case 'info':
      default:
        return <Info className="w-12 h-12 text-blue-500" />;
    }
  };

  return (
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal Content */}
      <div className={`relative w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 transform ${isAnimating ? 'scale-100 translate-y-0' : 'scale-90 translate-y-8'}`}>
        {/* Decorative Gradient Top Bar */}
        <div className={`h-2 w-full ${
          type === 'success' ? 'bg-green-500' : 
          type === 'error' ? 'bg-red-500' : 
          type === 'warning' ? 'bg-yellow-500' : 
          'bg-blue-600'
        }`} />

        <div className="p-8">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X size={20} />
          </button>

          <div className="flex flex-col items-center text-center space-y-4">
            <div className={`p-4 rounded-full ${
              type === 'success' ? 'bg-green-50 dark:bg-green-900/20' : 
              type === 'error' ? 'bg-red-50 dark:bg-red-900/20' : 
              type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20' : 
              'bg-blue-50 dark:bg-blue-900/20'
            }`}>
              {getIcon()}
            </div>

            <div className="space-y-2 w-full">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {title || 'Notice'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {message}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full pt-4">
              {onCancel && (
                <button
                  onClick={handleCancel}
                  className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all rounded-xl border border-transparent"
                >
                  {cancelText}
                </button>
              )}
              <button
                onClick={handleConfirm}
                className={`flex-1 px-6 py-3 text-sm font-semibold text-white transition-all rounded-xl shadow-lg shadow-blue-500/20 active:scale-95 ${
                  type === 'success' ? 'bg-green-600 hover:bg-green-700' : 
                  type === 'error' ? 'bg-red-600 hover:bg-red-700' : 
                  type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' : 
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
